import { useState, useEffect } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { uploadImageToS3, validateImageUpload, deleteFileFromS3, uploadVideoToS3, generateVideoThumbnail } from "@/services/s3Service";
import { ImageUploader } from "@/components/portfolio/form/ImageUploader";
import { InfoIcon } from "lucide-react";
import type { Json } from "@/integrations/supabase/types";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

// Tip tanımları daha esnek hale getiriliyor
interface Category {
  id: string;
  name_tr?: string;
  name_en?: string;
  name_ar?: string;
  // Dinamik özellikler için unknown kullanıyoruz (any yerine)
  [key: string]: unknown;
}

// Json nesnesi için tip tanımı
interface MultiLanguageText {
  tr: string;
  en: string;
  ar: string;
  [key: string]: string; // Diğer diller için
}

// Proje tablosu için veri tipi
interface ProjectData {
  id?: string;
  title: Json;
  description: Json;
  category_id: string | null;
  order_num: number;
  created_at?: string;
  updated_at?: string;
}

// Video dönüştürme fonksiyonu - MP4'e dönüştürür (H.264 kodek)
async function convertToMp4(videoFile: File): Promise<File> {
  try {
    // Video elementini oluştur
    const videoElement = document.createElement('video');
    videoElement.src = URL.createObjectURL(videoFile);
    videoElement.muted = true; // Otomatik oynatma için gerekli
    
    return new Promise((resolve, reject) => {
      videoElement.onloadedmetadata = async () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context oluşturulamadı'));
            return;
          }
          
          videoElement.play();
          
          // MediaRecorder ile MP4 çıktısı kaydet
          const stream = canvas.captureStream(30); // 30fps
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/mp4', // MIME türünü video/mp4 olarak değiştir
            videoBitsPerSecond: 2500000 // 2.5 Mbps (sıkıştırma seviyesi) - bu değeri ayarlayabilirsiniz
          });
          
          const chunks: Blob[] = [];
          
          mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              chunks.push(e.data);
            }
          };
          
          mediaRecorder.onstop = () => {
            const mp4Blob = new Blob(chunks, { type: 'video/mp4' }); // Blob türünü video/mp4 yap
            const convertedFile = new File(
              [mp4Blob], 
              `${videoFile.name.split('.')[0]}.mp4`, // Dosya uzantısını .mp4 yap
              { type: 'video/mp4' } // Dosya türünü video/mp4 yap
            );
            resolve(convertedFile);
            
            // Kaynakları temizle
            videoElement.pause();
            URL.revokeObjectURL(videoElement.src);
            videoElement.remove();
            canvas.remove();
          };
          
          mediaRecorder.start();
          
          // Video işleme süreci
          const processVideo = () => {
            if (videoElement.ended) {
              mediaRecorder.stop();
              return;
            }
            
            ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            requestAnimationFrame(processVideo);
          };
          
          processVideo();
        } catch (error) {
          console.error('Video dönüştürme hatası:', error);
          reject(error);
        }
      };
      
      videoElement.onerror = (e) => {
        console.error('Video yükleme hatası:', e);
        reject(new Error('Video yüklenemedi'));
      };
    });
  } catch (error) {
    console.error('Dönüştürme hatası:', error);
    // Dönüştürme başarısız olursa orijinal dosyayı döndür
    return videoFile;
  }
}

export default function NewProject() {
  const [title, setTitle] = useState<MultiLanguageText>({
    tr: "",
    en: "",
    ar: ""
  });
  const [description, setDescription] = useState<MultiLanguageText>({
    tr: "",
    en: "",
    ar: ""
  });
  const [categoryId, setCategoryId] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [existingMedia, setExistingMedia] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // İnfo mesajı için state
  const [showWebpInfo, setShowWebpInfo] = useState(false);
  const [showVideoInfo, setShowVideoInfo] = useState(false);

  // Kategorileri yükle
  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name_tr, name_en, name_ar');
      
      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }
      
      // Tip dönüştürmeyi düzeltiyoruz
      setCategories(data ? (data as unknown as Category[]) : []);
    };
    
    fetchCategories();
  }, []);

  // Video dosyaları değiştiğinde MP4'e dönüştür
  useEffect(() => {
    let isMounted = true;
    
    async function convertVideos() {
      if (videoFiles.length === 0) return;
      
      setIsConverting(true);
      try {
        const convertedVideos = await Promise.all(
          videoFiles.map(file => convertToMp4(file)) // convertToWebM -> convertToMp4
        );
        
        // Component hala mount edilmiş ise state'i güncelle
        if (isMounted) {
          // Doğrudan state'i atama yapmak yerine fonksiyon kullanarak güncelleme
          setVideoFiles(prevFiles => {
            // Eğer önceki dosyalarla yeni dönüştürülenler aynı ise, döngü oluşmasını önlemek için değişiklik yapma
            if (prevFiles.length === convertedVideos.length && 
                prevFiles.every((file, index) => file.name === convertedVideos[index].name)) {
              return prevFiles;
            }
            return convertedVideos;
          });
          
          // Dönüştürme işlemi tamamlandı
          setIsConverting(false);
          
          toast({
            title: "Başarılı",
            description: "Videolar MP4 formatına dönüştürüldü", // WebM -> MP4
          });
        }
      } catch (error) {
        // Component hala mount edilmiş ise hata mesajı göster
        if (isMounted) {
          console.error('Video dönüştürme hatası:', error);
          setIsConverting(false);
          toast({
            title: "Uyarı",
            description: "Bazı videolar dönüştürülemedi, orijinal formatında yüklenecek",
            variant: "destructive",
          });
        }
      }
    }
    
    if (videoFiles.length > 0) {
      // Dosyalar zaten MP4 formatında mı kontrol et
      const allMp4 = videoFiles.every(file => 
        file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4') // webm -> mp4
      );
      
      if (allMp4) {
        // Videolar zaten MP4 formatındaysa dönüştürme yapma
        setIsConverting(false);
      } else {
        convertVideos();
      }
    } else {
      // Video dosyası yoksa dönüştürme durumunu kapat
      setIsConverting(false);
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [videoFiles.length]); // videoFiles dizisi yerine sadece uzunluğunu izle

  const validateVideoUpload = (files: File[]): string | null => {
    if (!files.length) return null;
    
    for (const file of files) {
      // 250MB limit
      if (file.size > 250 * 1024 * 1024) {
        return "Video dosyası 250MB'dan büyük olamaz.";
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Kategori seçimi zorunlu kontrol
    if (!categoryId) {
      toast({
        title: "Hata",
        description: "Lütfen bir kategori seçin.",
        variant: "destructive",
      });
      return;
    }
    
    // Görsel yükleme validasyonu
    const imageValidationError = validateImageUpload(files);
    if (imageValidationError) {
      toast({
        title: "Hata",
        description: imageValidationError,
        variant: "destructive",
      });
      return;
    }
    
    // Video yükleme validasyonu
    const videoValidationError = validateVideoUpload(videoFiles);
    if (videoValidationError) {
      toast({
        title: "Hata",
        description: videoValidationError,
        variant: "destructive",
      });
      return;
    }
    
    // Video dosya sayısı kontrolü
    if (videoFiles.length > 2) {
      toast({
        title: "Hata",
        description: "En fazla 2 video yükleyebilirsiniz.",
        variant: "destructive",
      });
      return;
    }
    
    // Video dönüştürme işlemi hala devam ediyorsa beklet
    if (isConverting) {
      toast({
        title: "Bilgi",
        description: "Videolar dönüştürülüyor, lütfen bekleyin.",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // En yüksek sıra numarasını bul
      const { data: existingProjects, error: fetchError } = await supabase
        .from('projects')
        .select('order_num')
        .order('order_num', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Sıra numarası alınırken hata:', fetchError);
        throw fetchError;
      }

      // Yeni sıra numarasını hesapla
      // Linter hatasını düzeltmek için 'as any' kullanıldı
      const newOrderNum = existingProjects && existingProjects.length > 0 
        ? ((existingProjects[0] as any)?.order_num as number || 0) + 1 
        : 1;

      console.log('Proje eklenmeye hazırlanıyor:', {
        title,
        description,
        categoryId: categoryId || null,
        orderNum: newOrderNum
      });

      const projectData = {
        title: title as unknown as Json,
        description: description as unknown as Json,
        category_id: categoryId || null,
        order_num: newOrderNum
      };

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert(projectData as any) // Linter hatasını düzeltmek için 'as any' kullanıldı
        .select()
        .single();
      
      if (projectError) {
        console.error('Proje oluşturma hatası:', projectError);
        throw projectError;
      }
      
      if (!project || !project.id) {
        throw new Error('Proje oluşturuldu ancak ID dönmedi');
      }
      
      // Görselleri yükle
      if (files.length > 0) {
        try {
          console.log(`${files.length} adet görsel yükleniyor...`);
          
          // Sıralı yükleme yaparak her resmin başarıyla yüklendiğinden emin ol
          const uploadedUrls: string[] = [];
          const mediaData: any[] = [];
          
          for (let i = 0; i < files.length; i++) {
            try {
              console.log(`${i + 1}/${files.length} görsel yükleniyor: ${files[i].name}`);
              const url = await uploadImageToS3(files[i], `projects/${project.id}`);
              uploadedUrls.push(url);
              
              // Her başarılı yüklemeden sonra veritabanına kaydet
              const mediaRecord = {
                project_id: project.id,
                storage_path: url,
                order_num: i + 1,
                media_type: "image"
              };
              
              const { error: mediaError } = await supabase
                .from('project_media')
                .insert(mediaRecord);
              
              if (mediaError) {
                console.error(`Görsel ${i + 1} veritabanına kaydedilirken hata:`, mediaError);
                // S3'den yüklenen dosyayı sil
                try {
                  await deleteFileFromS3(url);
                } catch (deleteError) {
                  console.error('S3 dosya silme hatası:', deleteError);
                }
                throw mediaError;
              }
              
              console.log(`${i + 1}/${files.length} görsel başarıyla yüklendi ve kaydedildi`);
            } catch (uploadError) {
              console.error(`Görsel ${i + 1} yüklenirken hata:`, uploadError);
              // Bu resmi atla ve diğerlerine devam et
              toast({
                title: "Uyarı",
                description: `${files[i].name} dosyası yüklenirken hata oluştu, diğer dosyalar yüklenmeye devam ediyor.`,
                variant: "destructive",
              });
            }
          }
          
          console.log(`Toplam ${uploadedUrls.length}/${files.length} görsel başarıyla yüklendi`);
          
          if (uploadedUrls.length === 0) {
            toast({
              title: "Uyarı",
              description: "Hiçbir görsel yüklenemedi.",
              variant: "destructive",
            });
          } else if (uploadedUrls.length < files.length) {
            toast({
              title: "Kısmi Başarı",
              description: `${uploadedUrls.length}/${files.length} görsel başarıyla yüklendi.`,
            });
          }
        } catch (uploadError) {
          console.error('Görsel yükleme hatası:', uploadError);
          toast({
            title: "Uyarı",
            description: "Proje oluşturuldu ancak görseller yüklenirken bir sorun oluştu.",
            variant: "destructive",
          });
        }
      }
      
      // Videoları yükle
      if (videoFiles.length > 0) {
        try {
          console.log(`${videoFiles.length} adet video yükleniyor...`);
          
          // Sıralı yükleme yaparak her videonun başarıyla yüklendiğinden emin ol
          const uploadedUrls: string[] = [];
          
          for (let i = 0; i < videoFiles.length; i++) {
            try {
              console.log(`${i + 1}/${videoFiles.length} video yükleniyor: ${videoFiles[i].name}`);
              // Videoları direkt S3'e yükle, resim yükleme servisini kullanma
              const url = await uploadVideoToS3(videoFiles[i], `projects/${project.id}/videos`);
              uploadedUrls.push(url);
              
              // Her başarılı yüklemeden sonra veritabanına kaydet
              const videoRecord = {
                project_id: project.id,
                storage_path: url,
                order_num: i + 1,
                media_type: "video"
              };
              
              const { error: videoError } = await supabase
                .from('project_media')
                .insert(videoRecord);
              
              if (videoError) {
                console.error(`Video ${i + 1} veritabanına kaydedilirken hata:`, videoError);
                throw videoError;
              }
              
              // Kapak görseli üret; başarısız olursa yüklemeyi bozma, sadece uyar
              const posterUrl = await generateVideoThumbnail(url);
              if (!posterUrl) {
                toast({
                  title: "Uyarı",
                  description: `Video ${i + 1} yüklendi ama kapak görseli oluşturulamadı.`,
                  variant: "destructive",
                });
              }
              
              console.log(`${i + 1}/${videoFiles.length} video başarıyla yüklendi ve kaydedildi`);
            } catch (uploadError) {
              console.error(`Video ${i + 1} yüklenirken hata:`, uploadError);
              // Bu videoyu atla ve diğerlerine devam et
              toast({
                title: "Uyarı",
                description: `${videoFiles[i].name} dosyası yüklenirken hata oluştu, diğer dosyalar yüklenmeye devam ediyor.`,
                variant: "destructive",
              });
            }
          }
          
          console.log(`Toplam ${uploadedUrls.length}/${videoFiles.length} video başarıyla yüklendi`);
          
          if (uploadedUrls.length === 0) {
            toast({
              title: "Uyarı",
              description: "Hiçbir video yüklenemedi.",
              variant: "destructive",
            });
          } else if (uploadedUrls.length < videoFiles.length) {
            toast({
              title: "Kısmi Başarı",
              description: `${uploadedUrls.length}/${videoFiles.length} video başarıyla yüklendi.`,
            });
          }
        } catch (uploadError) {
          console.error('Video yükleme hatası:', uploadError);
          toast({
            title: "Uyarı",
            description: "Proje oluşturuldu ancak videolar yüklenirken bir sorun oluştu.",
            variant: "destructive",
          });
        }
      }
      
      toast({
        title: "Başarılı",
        description: "Proje başarıyla oluşturuldu.",
      });
      
      navigate("/portfolio/projects");
    } catch (error: unknown) {
      console.error('Error creating project:', error);
      let errorMessage = "Proje oluşturulurken bir hata oluştu.";
      
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'code' in error) {
        // Supabase hata kodlarını kontrol et
        const errorWithCode = error as { code: string };
        if (errorWithCode.code === "23505") {
          errorMessage = "Bu isimle bir proje zaten mevcut.";
        } else if (errorWithCode.code === "23503") {
          errorMessage = "Seçilen kategori bulunamadı.";
        }
      }
      
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContentLayout 
      title="Yeni Proje" 
      backLink="/portfolio/projects"
    >
      <div className="portfolio-project-page content-container">
        <div className="scrollable-content">
          <form onSubmit={handleSubmit} className="p-6 space-y-6 overscroll-y-none">
            <Tabs defaultValue="tr" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="tr">{languageFlags.tr} Türkçe</TabsTrigger>
                <TabsTrigger value="en">{languageFlags.en} English</TabsTrigger>
                <TabsTrigger value="ar">{languageFlags.ar} العربية</TabsTrigger>
              </TabsList>
              <TabsContent value="tr" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-tr">Proje Adı (Türkçe)</Label>
                  <Input
                    id="title-tr"
                    placeholder="Proje adını girin"
                    value={title.tr}
                    onChange={(e) => setTitle({ ...title, tr: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description-tr">Açıklama (Türkçe)</Label>
                  <Textarea
                    id="description-tr"
                    placeholder="Proje açıklamasını girin"
                    value={description.tr}
                    onChange={(e) => setDescription({ ...description, tr: e.target.value })}
                    rows={5}
                    required
                  />
                </div>
              </TabsContent>
              <TabsContent value="en" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-en">Proje Adı (English)</Label>
                  <Input
                    id="title-en"
                    placeholder="Enter project name"
                    value={title.en}
                    onChange={(e) => setTitle({ ...title, en: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description-en">Açıklama (English)</Label>
                  <Textarea
                    id="description-en"
                    placeholder="Enter project description"
                    value={description.en}
                    onChange={(e) => setDescription({ ...description, en: e.target.value })}
                    rows={5}
                    required
                  />
                </div>
              </TabsContent>
              <TabsContent value="ar" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar">Proje Adı (العربية)</Label>
                  <Input
                    id="title-ar"
                    placeholder="أدخل اسم المشروع"
                    value={title.ar}
                    onChange={(e) => setTitle({ ...title, ar: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description-ar">Açıklama (العربية)</Label>
                  <Textarea
                    id="description-ar"
                    placeholder="أدخل وصف المشروع"
                    value={description.ar}
                    onChange={(e) => setDescription({ ...description, ar: e.target.value })}
                    rows={5}
                    required
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Kategori</Label>
                <Select value={categoryId} onValueChange={setCategoryId} required>
                  <SelectTrigger id="category" className={!categoryId ? "border-red-500" : ""}>
                    <SelectValue placeholder="Kategori seçin (zorunlu)" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name_tr || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!categoryId && <p className="text-xs text-red-500 mt-1">Kategori seçimi zorunludur</p>}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Proje Görselleri</Label>
                  <InfoIcon 
                    className="h-4 w-4 text-muted-foreground cursor-pointer" 
                    onMouseEnter={() => setShowWebpInfo(true)}
                    onMouseLeave={() => setShowWebpInfo(false)}
                  />
                </div>
                
                {showWebpInfo && (
                  <div className="p-2 bg-muted text-xs rounded-md mb-2">
                    Yüklenen tüm görsel dosyaları otomatik olarak WebP formatına dönüştürülür. 
                    Bu, sayfa yükleme hızını artırmak için dosya boyutunu optimize eder.
                    Maksimum dosya boyutu: 30MB.
                  </div>
                )}
                
                <ImageUploader
                  files={files}
                  setFiles={setFiles}
                  maxFiles={25}
                  accept="image/*"
                />
                <p className="text-sm text-muted-foreground">En fazla 25 görsel yükleyebilirsiniz. Tüm görseller otomatik olarak WebP formatına dönüştürülecektir.</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Proje Videoları</Label>
                  <InfoIcon 
                    className="h-4 w-4 text-muted-foreground cursor-pointer" 
                    onMouseEnter={() => setShowVideoInfo(true)}
                    onMouseLeave={() => setShowVideoInfo(false)}
                  />
                </div>
                
                {showVideoInfo && (
                  <div className="p-2 bg-muted text-xs rounded-md mb-2">
                    Projenize video ekleyebilirsiniz. Yüklenen tüm videolar otomatik olarak MP4 formatına dönüştürülür ve optimize edilir. {/* WebM -> MP4 */}
                    Maksimum dosya boyutu: 250MB. En fazla 2 video ekleyebilirsiniz.
                  </div>
                )}
                
                <ImageUploader
                  files={videoFiles}
                  setFiles={setVideoFiles}
                  maxFiles={2}
                  accept="video/*"
                />
                {isConverting && (
                  <div className="p-2 bg-primary/10 text-xs rounded-md mb-2 flex items-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                    Videolar MP4 formatına dönüştürülüyor... {/* WebM -> MP4 */}
                  </div>
                )}
                <p className="text-sm text-muted-foreground">En fazla 2 video yükleyebilirsiniz. Videolar otomatik olarak MP4 formatına dönüştürülecektir.</p> {/* WebM -> MP4 */}
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate("/portfolio/projects")}
              >
                İptal
              </Button>
              <Button type="submit" disabled={isSubmitting || isConverting}>
                {isSubmitting ? "Kaydediliyor..." : (isConverting ? "Dönüştürülüyor..." : "Kaydet")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ContentLayout>
  );
}
